// src/components/Home/Section4.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Img1 from "../../assets/img/section4/image1.png";
import Img2 from "../../assets/img/section4/image2.png";
import Img3 from "../../assets/img/section4/image3.png";
import Img4 from "../../assets/img/section4/image4.png";
import Img5 from "../../assets/img/section4/image5.png";
import Img6 from "../../assets/img/section4/image6.png";
import Img7 from "../../assets/img/section4/image7.png";
import Img8 from "../../assets/img/section4/image8.png";
import Img9 from "../../assets/img/section4/image9.png";
import Img10 from "../../assets/img/section4/image10.png";
import Img11 from "../../assets/img/section4/image11.png";
import Img12 from "../../assets/img/section4/image12.png";

const logos = [
  { id: 1, name: "SLB", src: Img1 },
  { id: 2, name: "Petroplastic", src: Img2 },
  { id: 3, name: "Ucoop Gas", src: Img3 },
  { id: 4, name: "Atanor", src: Img4 },
  { id: 5, name: "Dioxitek", src: Img5 },
  { id: 6, name: "NA-SA", src: Img6 },
  { id: 7, name: "JLA", src: Img7 },
  { id: 8, name: "Agropaternal", src: Img8 },
  { id: 9, name: "Hidro-Grubert", src: Img9 },
  { id: 10, name: "Klockner Pentaplast", src: Img10 },
  { id: 11, name: "Induquim", src: Img11 },
  { id: 12, name: "TechOil", src: Img12 },
];

const chips = [
  "INDUSTRIA OIL&GAS",
  "INDUSTRIA QUÍMICA",
  "INDUSTRIA NUCLEAR",
  "INDUSTRIA AGROINDUSTRIAL",
  "INDUSTRIA AERONÁUTICA",
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Counter con animación
const Counter = ({ end = 0, duration = 1600, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let rafId;
    const start = performance.now();
    const animate = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(end * eased));
      if (t < 1) rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [isInView, duration, end]);

  return (
    <span ref={ref} className={className}>
      {value}
    </span>
  );
};

const Section4 = () => {
  return (
    <section
      id="clientes"
      className="min-h-screen w-full flex items-center justify-center"
    >
      <div className="w-full max-w-[1200px] px-4 sm:px-12 mx-auto flex flex-col items-center justify-center">
        {/* Frase superior */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-white/90 text-center italic font-inter text-lg sm:text-xl mb-6 sm:mb-10"
        >
          La confianza de grandes empresas,
          <span className="sm:hidden">
            <br />
          </span>
          nuestro mejor aval.
        </motion.p>

        {/* Card blanca con logos */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="bg-white rounded-[20px] sm:rounded-[28px] px-4 sm:px-10 py-6 sm:py-20 shadow-[0_10px_30px_rgba(0,0,0,.18)] mb-6 sm:mb-10 w-full"
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8 sm:gap-16 items-center justify-items-center">
            {logos.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.05 * i }}
                className="w-full flex items-center justify-center"
              >
                <img
                  src={l.src}
                  alt={l.name}
                  className="max-h-14 sm:max-h-20 object-contain hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Chips inferiores */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-col items-center justify-center mt-5 space-y-4 sm:space-y-6"
        >
          {/* Fila superior */}
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap justify-center gap-x-8 sm:gap-x-10 gap-y-3 w-full sm:w-auto">
            {chips.slice(0, 3).map((c) => {
              const [first, second] = c.split(" ");
              return (
                <span
                  key={c}
                  className="mx-auto sm:mx-0 px-6 sm:px-8 py-1.5 sm:py-2 rounded-full bg-white text-[#0B0A0B] text-xs sm:text-sm font-bai italic shadow-[0_0_0_1px_rgba(0,0,0,.08)] text-center"
                >
                  {first} <strong>{second}</strong>
                </span>
              );
            })}
          </div>

          {/* Fila inferior */}
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap justify-center gap-x-8 sm:gap-x-10 gap-y-3 w-full sm:w-auto">
            {chips.slice(3).map((c) => {
              const [first, second] = c.split(" ");
              return (
                <span
                  key={c}
                  className="mx-auto sm:mx-0 px-6 sm:px-8 py-1.5 sm:py-2 rounded-full bg-white text-[#0B0A0B] text-xs sm:text-sm font-bai italic shadow-[0_0_0_1px_rgba(0,0,0,.08)] text-center"
                >
                  {first} <strong>{second}</strong>
                </span>
              );
            })}
          </div>
        </motion.div>

        {/* MÉTRICAS (contadores) - ahora debajo de los chips */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="w-full mt-10 sm:mt-14"
        >
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-10 items-center justify-items-center text-center">
            {/* Años de trayectoria */}
            <div className="flex flex-col items-center">
              <Counter
                end={4}
                className="font-bai text-[38px] leading-none text-[#21C0A8]"
              />
              <span className="mt-2 text-white tracking-wide text-sm sm:text-base font-bai uppercase">
                AÑOS DE TRAYECTORIA
              </span>
            </div>

            {/* Proyectos desarrollados */}
            <div className="flex flex-col items-center">
              <Counter
                end={127}
                className="font-bai text-[38px] leading-none text-[#21C0A8]"
              />
              <span className="mt-2 text-white tracking-wide text-sm sm:text-base font-bai uppercase">
                PROYECTOS DESARROLLADOS
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Section4;